import i18next, { TFunction } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import i18nextBackend from 'i18next-http-backend';
import dayjs from './dayjs';
import { initReactI18next } from 'react-i18next';

import { environment } from '../../environments/environment';
import { setCurrencyLocale } from './currency';

export enum I18nNameSpaces {
  Common = 'common',
  Auth = 'auth',
  Forms = 'forms',
  PDF = 'pdf',
  Fonds = 'fonds',
}

export enum Languages {
  Czech = 'cz',
  Slovak = 'sk',
}

type LanguagesConfig = Record<
  string,
  {
    currencySymbol: string;
    dayjsLocale: string;
    intlLocale: string;
    resourceLocale: string;
  }
>;

export const LANGUAGE_CURRENCY_MAP = {
  [Languages.Czech]: 'CZK',
  [Languages.Slovak]: 'EUR',
};

export const LANGUAGES: LanguagesConfig = {
  [Languages.Czech]: {
    resourceLocale: 'cz',
    dayjsLocale: 'cs',
    intlLocale: 'cs-CZ',
    currencySymbol: ' Kč',
  },
  [Languages.Slovak]: {
    resourceLocale: 'sk',
    dayjsLocale: 'sk',
    intlLocale: 'sk-SK',
    currencySymbol: '€',
  },
};

// See: https://www.i18next.com/overview/configuration-options
const i18nInstance = i18next
  .use(i18nextBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: false,
    ns: Object.values(I18nNameSpaces),
    defaultNS: I18nNameSpaces.Common,
    nsSeparator: ':',
    returnEmptyString: true,
    detection: {
      order: ['cookie', 'navigator'],
      lookupCookie: 'phoenix-lang',
      caches: ['cookie'],
      cookieMinutes: 525_600,
    },
    initAsync: false,
    supportedLngs: [Languages.Czech, Languages.Slovak],
    fallbackLng: Languages.Czech,
    lng: window.location.host.endsWith('.sk') ? Languages.Slovak : Languages.Czech,
    react: {
      useSuspense: false,
    },
    interpolation: { escapeValue: false },
    missingInterpolationHandler: () => '',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      queryStringParams: { v: import.meta.env.VITE_LOCALE_CACHE_VERSION },
    },
  });

if (environment.debug) {
  (window as any).dayjs = dayjs;
  (window as any).i18n = i18next;
}

i18next.on('languageChanged', (lng) => {
  dayjs.locale(LANGUAGES[lng].dayjsLocale);
  setCurrencyLocale({
    intlLocale: LANGUAGES[lng].intlLocale,
    symbol: LANGUAGES[lng].currencySymbol,
  });
});

export function getFormsTranslator(): TFunction {
  return i18next.getFixedT(i18next.language, I18nNameSpaces.Forms);
}

export default i18nInstance;
